import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  LinearProgress,
  Avatar,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarAngleAxis,
  PolarRadiusAxis,
  PolarGrid,
  Radar,
} from "recharts";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PersonIcon from "@mui/icons-material/Person";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";

import SelectInput from "../InputComponents/SelectInput";
import { axiosAPI } from "../AxiosComponents/Axios";
import { useStaffLayout } from "../Context/CandidateLayoutContext";
import Loader from "../Suspense/Loader";
import CountUp from "react-countup";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const COLORS = [
    "#7063D4",
    "#C274E1",
    "#8AE073",
    "#F48000",
    "#5C7DFB",

    "#1e8a00ff",
    "#30B0C7",
    "#EAEB63",
    "#E56B76",
  ];

  const navigate = useNavigate();
  const id =
    localStorage?.getItem("SWA_UserId") ||
    sessionStorage?.getItem("SWA_UserId");

  const navigateToClients = () => {
    navigate(`/Clients`);
  };

  const navigateToStaff = () => {
    navigate(`/Staffs`);
  };

  const navigateToCandidate = () => {
    navigate(`/Candidates`);
  };

  const navigateToDeployedCandidate = () => {
    navigate(`/Candidates`, { state: { isDeployed: `deployed` } });
  };

  const navigateToJobs = () => {
    navigate(`/Jobs`);
  };

  const [year1, setYear1] = useState("2025");
  const [year2, setYear2] = useState("2025");

  const [month, setMonth] = useState(() => {
    const currentMonth = new Date().getMonth() + 1; // 0 = Jan → +1 = 1–12
    return String(currentMonth); // "01", "02", ...
  });

  const [year3, setYear3] = useState("2025");
  const [year4, setYear4] = useState("2025");

  const [selectedJob, setSelectedJob] = useState(null);

  const [clientProgress, setClientProgress] = useState([]);
  const [dashCountData, setDashCountData] = useState({});

  const [jobData, setJobData] = useState([]);
  const [jobList, setJobList] = useState([]);

  const [countryData, setCountryData] = useState([]);
  const [professionData, setProfessionData] = useState([]);

  const [yearOptions, setYearOptions] = useState([]);
  const [monthOptions, setMonthOptions] = useState([]);

  const getJobs = async () => {
    try {
      const res = await axiosAPI.get(`/Job/active/list`);
      const jobs = res?.data?.data || [];

      // Add "All Jobs" option at the beginning
      const jobListWithAll = [{ JD_Title: "All Jobs", JD_Id: null }, ...jobs];

      setJobList(jobListWithAll);
    } catch (err) {
      console.error(err);
    }
  };

  const getDashCount = async () => {
    try {
      const res = await axiosAPI?.get(`/dashboard/count`, {
        year: year1,
      });
      setDashCountData(res?.data?.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getDashChart = async () => {
    try {
      const res = await axiosAPI?.post(`/dashboard/registration-summary`, {
        year: year1,
      });

      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const formattedData = res?.data?.data?.map((item) => ({
        ...item,
        month: monthNames[item.month - 1],
      }));
      setJobData(formattedData);

      // setJobData(res?.data?.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getProfessionChart = () => {
    axiosAPI
      ?.post(`/dashboard/Profession`, { year: year4 })
      .then((res) => {
        const rawData = res?.data?.data;

        const formatted = Array.isArray(rawData)
          ? rawData
              .filter(
                (item) =>
                  item.Profession && typeof item.ApplicationCount === "number"
              )
              .map((item) => ({
                Profession: item.Profession,
                ApplicationCount: item.ApplicationCount,
              }))
          : [];

        setProfessionData(formatted);
      })
      .catch((err) => {
        console.error("Error fetching profession chart:", err);
        setProfessionData([]); // fallback to empty array
      });
  };

  const getCountryChart = () => {
    axiosAPI
      ?.post(`/dashboard/Country`, { year: year3 })
      .then((res) => {
        const rawData = res?.data?.data;

        const formatted = Array.isArray(rawData)
          ? rawData
              .filter(
                (item) =>
                  item.Country && typeof item.ApplicationCount === "number"
              )
              .map((item) => ({
                Country: item.Country,
                ApplicationCount: item.ApplicationCount,
              }))
          : [];

        setCountryData(formatted);
      })
      .catch((err) => {
        console.error("Error fetching country chart:", err);
        setCountryData([]); // fallback to empty array
      });
  };

  const getMonthlyCandidateStatistics = () => {
    axiosAPI
      ?.post(`/dashboard/Interview-summary`, {
        year: year2 || null,
        month: month || null,
        jobId: selectedJob || null,
      })
      .then((res) => {
        setClientProgress(res?.data?.data);
      })
      .catch((err) => {
        console.error("Error fetching monthly candidate statistics:", err);
      });
  };

  useEffect(() => {
    // Year options
    const currentYear = new Date().getFullYear();
    const startYear = 2020;
    const endYear = currentYear + 10;

    const yearOptions = Array.from(
      { length: endYear - startYear + 1 },
      (_, i) => {
        const year = startYear + i;
        return { label: String(year), value: String(year) };
      }
    );
    setYearOptions(yearOptions);

    // Month options
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const monthOptions = monthNames.map((month, index) => ({
      label: month,
      value: String(index + 1), // "01", "02", ...
    }));
    setMonthOptions(monthOptions);

    window.scrollTo(0, 0);
    getDashCount();
    getJobs();
  }, []);

  useEffect(() => {
    getDashChart();
  }, [year1]);

  useEffect(() => {
    getMonthlyCandidateStatistics();
  }, [year2, month, selectedJob]);

  useEffect(() => {
    getCountryChart();
  }, [year3]);

  useEffect(() => {
    getProfessionChart();
  }, [year4]);

  const getMax = (data = []) =>
    Math.max(
      ...(Array.isArray(data)
        ? data.map((item) => item?.ApplicationCount || 0)
        : [])
    ) || 1;

  const maxCountryCount = getMax(countryData);
  const maxProfessionCount = getMax(professionData);

  const cardData = [
    // {
    //   icon: <PeopleIcon fontSize="small" sx={{ color: "#399F8B" }} />,
    //   title: "Clients",
    //   value: dashCountData?.totalClients,
    //   bg: "#399F8B",
    //   color: "white",
    //   fncall: navigateToClients,
    // },

    {
      icon: <PeopleIcon sx={{ color: "#007AFF" }} />,
      title: "Active Candidates",
      value: dashCountData?.totalCandidates,
      bg: "#007AFF",
      color: "white",
      fncall: navigateToCandidate,
    },
    {
      icon: <FlightTakeoffIcon sx={{ color: "#5DAE4D" }} />,
      title: "Deployed Candidates",
      value: dashCountData?.totalDeployed,
      bg: "#5DAE4D",
      color: "white",
      fncall: navigateToDeployedCandidate,
    },
    {
      icon: <WorkIcon sx={{ color: "#20A8D8" }} />,
      title: "Jobs",
      value: dashCountData?.totalJobsPosted,
      bg: "#20A8D8",
      color: "white",
      fncall: navigateToJobs,
    },
    {
      icon: <PersonIcon sx={{ color: "#F3582E" }} />,
      title: "Staff",
      value: dashCountData?.totalStaffs - 1,
      bg: "#F3582E",
      color: "white",
      fncall: navigateToStaff,
    },
  ];

  return (
    <Paper
      sx={{
        padding: 2,
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
        maxWidth: "100vw",
        boxShadow: "none",
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          textAlign: "left",
          fontFamily: "Geist, sans-serif",
          color: "#333",
          fontWeight: "600",
        }}
      >
        Dashboard
      </Typography>

      <Grid container spacing={2}>
        {cardData.map((item, index) => (
          <Grid item xs={12} sm={6} md={6} lg={3} xl={3} key={index}>
            <Card
              sx={{
                backgroundColor: item.bg,
                boxShadow: "none",
                borderRadius: 3,
                height: "auto",
                maxHeight: "120px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                transition: "transform 0.2s ease-in-out",
                px: 1.5,
              }}
            >
              <CardContent
                sx={{
                  width: "100%",
                  minHeight: "100px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "space-around",
                  justifyContent: "center",
                  px: 2,
                }}
              >
                {/* Count and Title on same row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "space-between",
                    gap: 1.5,
                    my: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Geist, sans-serif",
                      fontSize: "20px",
                      fontWeight: "600",
                      color: item.bg,
                      background: "white",
                      borderRadius: "8px",
                      padding: "2px 10px",
                      minHeight: "30px",
                      // minWidth: "30px",
                    }}
                  >
                    <CountUp end={item.value} duration={1} separator="," />{" "}
                    {item.value != 0 ? "+" : ""}
                  </Typography>
                  {/* Icon on next line */}
                  <Avatar
                    sx={{
                      background: "white",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "scale(1.2)",
                      },
                      width: 36,
                      height: 36,
                    }}
                    onClick={item?.fncall}
                  >
                    {item?.icon}
                  </Avatar>
                </Box>

                <Typography
                  sx={{
                    fontFamily: "Geist, sans-serif",
                    fontSize: "20px",
                    fontWeight: "500",
                    color: item.color,
                  }}
                >
                  {item.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} mt={1}>
        {/* Candidate Registration Summary */}
        <Grid item xs={12} xl={6}>
          <Grid
            container
            sx={{
              border: "2px solid lightgrey",
              borderRadius: "10px",
              padding: "20px 10px",
              minHeight: { lg: "", xl: "450px" },
            }}
          >
            {/* Heading */}
            <Grid
              item
              mb={1}
              xs={12}
              sm={12}
              md={8}
              sx={{ padding: "0px 10px" }}
            >
              <Typography
                sx={{
                  fontFamily: "Geist, sans-serif",
                  color: "#333",
                  fontWeight: 600,
                  fontSize: "24px",
                }}
              >
                Candidate Summary
              </Typography>
            </Grid>

            {/* Year Selector */}
            <Grid
              item
              mb={2}
              xs={12}
              sm={12}
              md={4}
              sx={{ padding: "0px 10px" }}
            >
              <SelectInput
                label="Year"
                options={yearOptions}
                placeholder="Select Year"
                onChange={(e) => setYear1(e.target.value)}
                value={year1}
              />
            </Grid>

            {/* Chart */}
            <Grid
              item
              mb={1}
              xs={12}
              sx={{ position: "relative", right: "25px", top: "25px" }}
            >
              <ResponsiveContainer width="100%" height={300}>
                {jobData?.some(
                  (item) => item.totalCandidates !== 0 || item.totalJoined !== 0
                ) ? (
                  <BarChart data={jobData}>
                    <CartesianGrid stroke="lightgrey" strokeDasharray="5" />
                    <XAxis stroke="#333" dataKey="month" />
                    <YAxis stroke="#333" />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="totalCandidates" fill="#0099D3" name="New" />
                    <Bar dataKey="totalJoined" fill="#E26C09" name="Deployed" />
                    <Bar
                      dataKey="totalCancelled"
                      fill="#d82404ff"
                      name="Cancelled"
                    />
                  </BarChart>
                ) : (
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    sx={{ height: 300 }}
                  >
                    <Typography color="text.secondary">
                      No data to show
                    </Typography>
                  </Grid>
                )}
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </Grid>

        {/* Interview Outcome */}

        <Grid item xs={12} xl={6}>
          <Grid
            container
            sx={{
              border: "2px solid lightgrey",
              borderRadius: "10px",
              padding: "20px 10px",
              minHeight: { lg: "", xl: "450px" },
            }}
          >
            {/* Heading */}
            <Grid item mb={1} xs={12} sx={{ padding: "0px 10px" }}>
              <Typography
                sx={{
                  fontFamily: "Geist, sans-serif",
                  color: "#333",
                  fontWeight: 600,
                  fontSize: "24px",
                }}
              >
                Interview Outcome
              </Typography>
            </Grid>

            {/* Filters */}
            <Grid item mb={1} xs={12} md={4} sx={{ padding: "0px 10px" }}>
              <SelectInput
                value={selectedJob}
                name="jobid"
                onChange={(e) => setSelectedJob(e.target.value)}
                options={jobList?.map((item) => ({
                  value: item?.JD_Id,
                  label: item?.JD_Title,
                }))}
                placeholder="Select Job"
              />
            </Grid>

            <Grid item mb={1} xs={12} md={4} sx={{ padding: "0px 10px" }}>
              <SelectInput
                label="Year"
                options={yearOptions}
                placeholder="Select Year"
                onChange={(e) => setYear2(e.target.value)}
                value={year2}
              />
            </Grid>

            <Grid item mb={1} xs={12} md={4} sx={{ padding: "0px 10px" }}>
              <SelectInput
                label="Month"
                options={monthOptions}
                placeholder="Select Month"
                onChange={(e) => setMonth(e.target.value)}
                value={month}
              />
            </Grid>

            {/* Chart */}
            <Grid item mb={1} xs={12}>
              {clientProgress?.every((item) => item.count == 0) ? (
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  sx={{ height: 300 }}
                >
                  <Typography color="text.secondary">
                    No data to show
                  </Typography>
                </Grid>
              ) : (
                <Grid
                  container
                  spacing={2}
                  sx={{ position: "relative", top: "20px" }}
                >
                  {/* Stacked Bar */}
                  <Grid item xs={12} sm={12} md={9} lg={9} xl={8}>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={[
                          clientProgress.reduce((acc, curr) => {
                            acc[curr.status] = curr.count;
                            return acc;
                          }, {}),
                        ]}
                        margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={() => "Interview Outcome"} />
                        <YAxis />
                        <Tooltip />
                        {clientProgress?.map((entry, index) => (
                          <Bar
                            key={entry.status}
                            dataKey={entry.status}
                            stackId="a"
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Grid>

                  {/* Legend */}
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={4}>
                    {clientProgress?.map((entry, index) => (
                      <Grid
                        container
                        key={entry.status}
                        alignItems="center"
                        sx={{
                          mb: 1,
                          position: "relative",
                          left: {
                            xs: "14px",
                            sm: "12px",
                            md: "0px",
                            lg: "12px",
                          },
                          top: { sm: 0, md: "20px" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            backgroundColor: COLORS[index % COLORS.length],
                            borderRadius: "50%",
                            mr: 1,
                          }}
                        />
                        <Typography variant="body2">
                          {entry.status} ({entry.count})
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={0.5}>
        {/* Top Recruiting Countries */}
        <Grid item xs={12} xl={6}>
          <Grid
            container
            sx={{
              border: "2px solid lightgrey",
              borderRadius: "10px",
              padding: "20px 10px",
            }}
          >
            {/* Heading */}
            <Grid
              item
              mb={1}
              xs={12}
              sm={12}
              md={8}
              sx={{ padding: "0px 10px" }}
            >
              <Typography
                sx={{
                  fontFamily: "Geist, sans-serif",
                  color: "#333",
                  fontWeight: 600,
                  fontSize: "24px",
                }}
              >
                Top Recruiting Countries
              </Typography>
            </Grid>

            {/* Filters */}
            <Grid
              item
              mb={2}
              xs={12}
              sm={12}
              md={4}
              sx={{ padding: "0px 10px" }}
            >
              <SelectInput
                label="Year"
                options={yearOptions}
                placeholder="Select Year"
                onChange={(e) => setYear3(e.target.value)}
                value={year3}
              />
            </Grid>

            {/* MUI Progress Bars for Country Data */}
            <Grid item xs={12} sx={{ padding: "0px 10px" }}>
              <Grid
                container
                spacing={1}
                sx={{
                  mt: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  minHeight: { lg: "", xl: "180px" },
                }}
              >
                {countryData?.length > 0 &&
                countryData?.some((item) => item.ApplicationCount !== 0) ? (
                  countryData?.map((item, index) => (
                    <Grid item xs={12} key={index}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Box sx={{ minWidth: 150 }}>
                          <Typography
                            sx={{
                              fontFamily: "Geist, sans-serif",
                              color: "#1c1c1c",
                              fontWeight: 400,
                              fontSize: "15px",
                            }}
                          >
                            {item.Country}
                          </Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1, mx: 2 }}>
                          <LinearProgress
                            variant="determinate"
                            value={
                              (item.ApplicationCount / maxCountryCount) * 100
                            }
                            sx={{
                              height: 10,
                              borderRadius: 5,
                              backgroundColor: "#f0f0f0",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "#0099D3",
                              },
                            }}
                          />
                        </Box>
                        <Box sx={{ minWidth: 35 }}>
                          <Typography
                            sx={{
                              fontFamily: "Geist, sans-serif",
                              color: "#1c1c1c",
                              fontWeight: 400,
                              fontSize: "18px",
                            }}
                          >
                            {item.ApplicationCount}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))
                ) : (
                  <Grid
                    item
                    xs={12}
                    container
                    justifyContent="center"
                    alignItems="center"
                    sx={{ height: 300 }}
                  >
                    <Typography color="text.secondary">
                      No data to show
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Top Recruiting Professions */}
        <Grid item xs={12} xl={6}>
          <Grid
            container
            sx={{
              border: "2px solid lightgrey",
              borderRadius: "10px",
              padding: "20px 10px",
            }}
          >
            {/* Heading */}
            <Grid
              item
              mb={1}
              xs={12}
              sm={12}
              md={8}
              sx={{ padding: "0px 10px" }}
            >
              <Typography
                sx={{
                  fontFamily: "Geist, sans-serif",
                  color: "#333",
                  fontWeight: 600,
                  fontSize: "24px",
                }}
              >
                Top Recruiting Professions
              </Typography>
            </Grid>

            {/* Filters */}
            <Grid
              item
              mb={2}
              xs={12}
              sm={12}
              md={4}
              sx={{ padding: "0px 10px" }}
            >
              <SelectInput
                label="Year"
                options={yearOptions}
                placeholder="Select Year"
                onChange={(e) => setYear4(e.target.value)}
                value={year4}
              />
            </Grid>

            {/* MUI Progress Bars for Profession Data */}
            <Grid item xs={12} sx={{ padding: "0px 10px" }}>
              <Grid
                container
                spacing={1}
                sx={{
                  mt: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  minHeight: { lg: "", xl: "180px" },
                }}
              >
                {professionData?.length > 0 &&
                professionData?.some((item) => item.ApplicationCount !== 0) ? (
                  professionData?.map((item, index) => (
                    <Grid item xs={12} key={index}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Box sx={{ minWidth: 150 }}>
                          <Typography
                            sx={{
                              fontFamily: "Geist, sans-serif",
                              color: "#1c1c1c",
                              fontWeight: 400,
                              fontSize: "15px",
                            }}
                          >
                            {item.Profession}
                          </Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1, mx: 2 }}>
                          <LinearProgress
                            variant="determinate"
                            value={
                              (item.ApplicationCount / maxProfessionCount) * 100
                            }
                            sx={{
                              height: 10,
                              borderRadius: 5,
                              backgroundColor: "#f0f0f0",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "#E65100",
                              },
                            }}
                          />
                        </Box>
                        <Box sx={{ minWidth: 35 }}>
                          <Typography
                            sx={{
                              fontFamily: "Geist, sans-serif",
                              color: "#1c1c1c",
                              fontWeight: 400,
                              fontSize: "18px",
                            }}
                          >
                            {item.ApplicationCount}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))
                ) : (
                  <Grid
                    item
                    xs={12}
                    container
                    justifyContent="center"
                    alignItems="center"
                    sx={{ height: 300 }}
                  >
                    <Typography color="text.secondary">
                      No data to show
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default Dashboard;
